import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';
import * as XLSX from 'xlsx';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';

export default function KeywordRelated() {
  const checkAuth = useAuthGuard();
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortCol, setSortCol] = useState('total');
  const [sortDesc, setSortDesc] = useState(true);
  const [mainDetail, setMainDetail] = useState<any>(null);
  const [mainPeriod, setMainPeriod] = useState('3m');
  const [mainLoading, setMainLoading] = useState(false);
  const [selDetail, setSelDetail] = useState<any>(null);
  const [selPeriod, setSelPeriod] = useState('3m');
  const [selLoading, setSelLoading] = useState(false);
  const [selCollapsed, setSelCollapsed] = useState(true);
  const [filterEnabled, setFilterEnabled] = useState(false);
  const [includeKeywords, setIncludeKeywords] = useState(['', '', '']);
  const [includeMode, setIncludeMode] = useState<'OR' | 'AND'>('OR');
  const [excludeKeywords, setExcludeKeywords] = useState(['', '', '']);
  const [excludeMode, setExcludeMode] = useState<'OR' | 'AND'>('OR');

  const getFiltered = (data: any[]) => {
    if (!filterEnabled) return data;
    const incl = includeKeywords.filter(k => k.trim());
    const excl = excludeKeywords.filter(k => k.trim());
    return data.filter((r: any) => {
      const name = r.keyword.toLowerCase();
      if (excl.length > 0) {
        const isExcluded = excludeMode === 'AND'
          ? excl.every(e => name.includes(e.toLowerCase()))
          : excl.some(e => name.includes(e.toLowerCase()));
        if (isExcluded) return false;
      }
      if (incl.length === 0) return true;
      if (includeMode === 'AND') return incl.every(i => name.includes(i.toLowerCase()));
      return incl.some(i => name.includes(i.toLowerCase()));
    });
  };

  const fetchDetail = async (kw: string, period: string) => {
    const res = await client.get(`/keyword/detail/${encodeURIComponent(kw)}?period=${period}`);
    return res.data;
  };

  const handleSearch = async () => {
    if (!checkAuth()) return;
    if (!keyword.trim()) return setError('키워드를 입력해주세요');
    setLoading(true); setMainLoading(true); setError('');
    setMainDetail(null); setSelDetail(null); setSelCollapsed(true);
    try {
      const [statsRes, detailRes] = await Promise.all([
        client.post('/keyword/stats', { keywords: [keyword.trim()] }),
        fetchDetail(keyword.trim(), mainPeriod),
      ]);
      setResults(statsRes.data.keywords);
      setMainDetail(detailRes);
    } catch (e: any) {
      setError(e.response?.data?.detail || '조회 실패');
    } finally { setLoading(false); setMainLoading(false); }
  };

  const handleSelectKeyword = async (kw: string) => {
    setSelCollapsed(false);
    setSelLoading(true);
    try {
      const res = await fetchDetail(kw, selPeriod);
      setSelDetail(res);
    } catch {} finally { setSelLoading(false); }
  };

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDesc(!sortDesc);
    else { setSortCol(col); setSortDesc(true); }
  };

  const filtered = getFiltered(results);
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortCol] ?? 0, bv = b[sortCol] ?? 0;
    if (typeof av === 'string') return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
    return sortDesc ? bv - av : av - bv;
  });
  const maxTotal = sorted.length > 0 ? Math.max(...sorted.map(r => r.total)) : 1;
  const compColor = (c: string) => c === '높음' ? '#F56565' : c === '중간' ? '#F6AD55' : '#00D4AA';

  const handleExcel = () => {
    if (!sorted.length) return;
    const data = sorted.map(r => ({
      '키워드': r.keyword, 'PC 검색량': r.pc, '모바일 검색량': r.mobile,
      '총 검색량': r.total, '모바일 비율(%)': r.mob_ratio, '경쟁도': r.competition,
      'PC 클릭수': r.pc_click, '모바일 클릭수': r.mob_click, '총 클릭수': r.total_click,
      'PC CTR(%)': r.pc_ctr, '모바일 CTR(%)': r.mob_ctr, '검색량 대비 클릭률(%)': r.total_ctr,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '연관키워드');
    XLSX.writeFile(wb, `연관키워드_${keyword}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGreen: { background: '#1A3A2A', border: '0.5px solid #2A5A3A', borderRadius: 8, padding: '10px 20px', color: '#00D4AA', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    input: { background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 12px', color: '#E8ECF4', fontSize: 14, width: '100%', boxSizing: 'border-box' as const },
    th: { color: '#4A5568', fontSize: 11, cursor: 'pointer', padding: '8px 6px', fontWeight: 400, userSelect: 'none' as const },
  };

  const ModeBtn = ({ mode, current, color, onClick }: any) => (
    <button onClick={onClick} style={{
      background: current === mode ? '#1A2035' : 'none',
      border: `1px solid ${current === mode ? color : '#2A3045'}`,
      borderRadius: 6, padding: '3px 12px',
      color: current === mode ? color : '#4A5568',
      fontSize: 12, cursor: 'pointer', fontWeight: current === mode ? 700 : 400
    }}>{mode}</button>
  );

  const PeriodBtns = ({ period, onChange }: any) => (
    <div style={{ display: 'flex', gap: 4 }}>
      {[['1m','1개월'],['3m','3개월'],['6m','6개월'],['1y','1년'],['3y','3년'],['5y','5년']].map(([p, label]) => (
        <button key={p} onClick={() => onChange(p)}
          style={{ padding: '3px 8px', borderRadius: 4, border: '0.5px solid #2A3045', fontSize: 11, cursor: 'pointer',
            background: period === p ? '#00D4AA' : 'transparent', color: period === p ? '#0A1F18' : '#8892A4', fontWeight: period === p ? 700 : 400 }}>
          {label}
        </button>
      ))}
    </div>
  );

  const DetailCard = ({ data, period, onPeriodChange, loading: dLoading, isMain, onClose }: any) => {
    if (!data) return null;
    const b = data.base || {};
    const pcVal = b.pc || 0;
    const mobVal = b.mobile || 0;
    const tot = pcVal + mobVal;
    const pcRatio = tot > 0 ? Math.round(pcVal / tot * 100) : 0;
    const mobileRatio = 100 - pcRatio;
    const deviceData = [
      { name: 'PC', value: pcRatio, color: '#378ADD' },
      { name: '모바일', value: mobileRatio, color: '#00D4AA' },
    ];
    const genderData = data.gender ? [
      { name: '남성', value: data.gender.male, color: '#378ADD' },
      { name: '여성', value: data.gender.female, color: '#F6AD55' },
    ] : [];

    return (
      <div style={{ background: '#161B27', border: `0.5px solid ${isMain ? '#00D4AA55' : '#2A3045'}`, borderRadius: 8, padding: 20, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMain
              ? <span style={{ background: '#00D4AA22', color: '#00D4AA', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>검색 키워드</span>
              : <span style={{ background: '#1A2035', color: '#378ADD', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>비교 키워드</span>
            }
            <span style={{ color: '#E8ECF4', fontSize: 15, fontWeight: 700 }}>"{data.keyword}"</span>
          </div>
          {!isMain && onClose && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4A5568', cursor: 'pointer', fontSize: 18 }}>✕</button>
          )}
        </div>

        {/* 기본지표 */}
        {b.total !== undefined && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
            {[
              { label: '총 검색량', value: (b.total||0).toLocaleString(), color: '#E8ECF4' },
              { label: 'PC 검색량', value: (b.pc||0).toLocaleString(), color: '#378ADD' },
              { label: '모바일 검색량', value: (b.mobile||0).toLocaleString(), color: '#00D4AA' },
              { label: '경쟁도', value: b.competition || '-', color: compColor(b.competition) },
              { label: '월평균 클릭수', value: (b.total_click||0).toLocaleString(), color: '#E8ECF4' },
              { label: '클릭률', value: `${b.total_ctr||0}%`, color: '#F6AD55' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#0F1117', borderRadius: 6, padding: '10px 12px', textAlign: 'center' as const }}>
                <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 4 }}>{label}</div>
                <div style={{ color, fontSize: 14, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* PC/모바일 도넛 + 성별 도넛 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#0F1117', borderRadius: 8, padding: 16 }}>
            <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 8 }}>PC / 모바일 비중</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <PieChart width={80} height={80}>
                <Pie data={deviceData} dataKey="value" innerRadius={25} outerRadius={38} startAngle={90} endAngle={-270} paddingAngle={2}>
                  {deviceData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
              </PieChart>
              <div>
                {deviceData.map(d => (
                  <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                    <span style={{ color: '#8892A4', fontSize: 11 }}>{d.name}</span>
                    <span style={{ color: d.color, fontSize: 13, fontWeight: 700 }}>{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ background: '#0F1117', borderRadius: 8, padding: 16 }}>
            <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 8 }}>성별 비중</div>
            {genderData.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <PieChart width={80} height={80}>
                  <Pie data={genderData} dataKey="value" innerRadius={25} outerRadius={38} startAngle={90} endAngle={-270} paddingAngle={2}>
                    {genderData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
                <div>
                  {genderData.map(d => (
                    <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                      <span style={{ color: '#8892A4', fontSize: 11 }}>{d.name}</span>
                      <span style={{ color: d.color, fontSize: 13, fontWeight: 700 }}>{d.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <div style={{ color: '#4A5568', fontSize: 12 }}>데이터 없음</div>}
          </div>
        </div>

        {/* 연령대 + 트렌드 한줄 배치 */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 7fr', gap: 12, marginBottom: 0 }}>
          {/* 연령대 */}
          {data.age && Object.keys(data.age).length > 0 ? (
            <div style={{ background: '#0F1117', borderRadius: 8, padding: 16 }}>
              <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 8 }}>연령대 비중</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={Object.entries(data.age).map(([k, v]) => ({ age: k, ratio: v }))} layout="vertical" margin={{ left: 0, right: 30 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="age" tick={{ fill: '#8892A4', fontSize: 11 }} width={40} />
                  <Tooltip formatter={(v: any) => [`${v}%`, '비중']}
                    contentStyle={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="ratio" radius={[0,3,3,0]} label={{ position: 'right', fill: '#8892A4', fontSize: 11, formatter: (v: any) => `${v}%` }}>
                    {Object.keys(data.age).map((_: any, i: number) => (
                      <Cell key={i} fill={['#00D4AA','#378ADD','#F6AD55','#F56565','#9F7AEA','#ED64A6'][i % 6]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div />}

          {/* 트렌드 */}
          <div style={{ background: '#0F1117', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ color: '#8892A4', fontSize: 12 }}>검색 트렌드 (상대값 0~100)</div>
              <PeriodBtns period={period} onChange={onPeriodChange} />
            </div>
            {dLoading ? (
              <div style={{ color: '#8892A4', fontSize: 12, textAlign: 'center' as const, padding: 40 }}>로딩 중...</div>
            ) : data.trend?.data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={data.trend.data}>
                  <XAxis dataKey="period" tick={{ fill: '#4A5568', fontSize: 9 }} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#4A5568', fontSize: 9 }} />
                  <Tooltip formatter={(v: any) => [v, '검색량']}
                    contentStyle={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, fontSize: 12 }} />
                  <Line type="monotone" dataKey="ratio" stroke="#00D4AA" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ color: '#4A5568', fontSize: 12, textAlign: 'center' as const, padding: 40 }}>데이터 없음</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div>
        <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>키워드 검색량 (연관)</h2>
        <p style={{ color: '#8892A4', fontSize: 12, margin: '0 0 20px' }}>키워드 1개로 연관 키워드와 검색량을 분석하세요</p>

        <div style={s.card}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <input value={keyword} onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="키워드 입력 후 엔터"
              style={{ flex: 1, ...s.input }} />
            <button style={s.btn} onClick={handleSearch} disabled={loading}>{loading ? '분석 중...' : '분석 시작'}</button>
            {sorted.length > 0 && <button style={s.btnGreen} onClick={handleExcel}>📥 엑셀</button>}
          </div>
          {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <div style={{ borderTop: '0.5px solid #1E2535', paddingTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: filterEnabled ? '#0F2A22' : '#0F1117', border: `1px solid ${filterEnabled ? '#00D4AA44' : '#2A3045'}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', marginBottom: filterEnabled ? 16 : 0 }}
              onClick={() => setFilterEnabled(f => !f)}>
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${filterEnabled ? '#00D4AA' : '#4A5568'}`, background: filterEnabled ? '#00D4AA' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {filterEnabled && <span style={{ color: '#0A1F18', fontSize: 12, fontWeight: 900 }}>✓</span>}
              </div>
              <div>
                <div style={{ color: filterEnabled ? '#00D4AA' : '#8892A4', fontSize: 13, fontWeight: 600 }}>결과 필터 적용 (선택사항)</div>
                <div style={{ color: '#4A5568', fontSize: 11, marginTop: 2 }}>포함/제외 키워드로 결과를 필터링합니다</div>
              </div>
            </div>
            {filterEnabled && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div><span style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 600 }}>✅ 필수 포함 키워드</span><span style={{ color: '#4A5568', fontSize: 11, marginLeft: 8 }}>최대 3개</span></div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <ModeBtn mode="OR" current={includeMode} color="#00D4AA" onClick={() => setIncludeMode('OR')} />
                      <ModeBtn mode="AND" current={includeMode} color="#00D4AA" onClick={() => setIncludeMode('AND')} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {includeKeywords.map((kw, i) => (
                      <input key={i} style={{ ...s.input, marginBottom: 0 }} value={kw}
                        onChange={e => { const n = [...includeKeywords]; n[i] = e.target.value; setIncludeKeywords(n); }}
                        placeholder={`포함 ${i + 1}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div><span style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 600 }}>❌ 제외 키워드</span><span style={{ color: '#4A5568', fontSize: 11, marginLeft: 8 }}>최대 3개</span></div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <ModeBtn mode="OR" current={excludeMode} color="#F56565" onClick={() => setExcludeMode('OR')} />
                      <ModeBtn mode="AND" current={excludeMode} color="#F56565" onClick={() => setExcludeMode('AND')} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {excludeKeywords.map((kw, i) => (
                      <input key={i} style={{ ...s.input, marginBottom: 0 }} value={kw}
                        onChange={e => { const n = [...excludeKeywords]; n[i] = e.target.value; setExcludeKeywords(n); }}
                        placeholder={`제외 ${i + 1}`} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 메인 키워드 세부분석 고정 */}
        {mainLoading && (
          <div style={{ ...s.card, textAlign: 'center' as const, color: '#8892A4', fontSize: 13 }}>세부 분석 중...</div>
        )}
        {mainDetail && !mainLoading && (
          <DetailCard data={mainDetail} period={mainPeriod} loading={mainLoading} isMain={true}
            onPeriodChange={async (p: string) => {
              setMainPeriod(p);
              setMainLoading(true);
              try { const res = await fetchDetail(mainDetail.keyword, p); setMainDetail(res); }
              finally { setMainLoading(false); }
            }} />
        )}

        {/* 비교 키워드 (접기/펼치기) */}
        {mainDetail && (
          <div style={{ background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', cursor: 'pointer' }}
              onClick={() => setSelCollapsed(c => !c)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: '#1A2035', color: '#378ADD', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>비교 키워드</span>
                <span style={{ color: selDetail ? '#E8ECF4' : '#4A5568', fontSize: 14 }}>
                  {selDetail ? `"${selDetail.keyword}"` : '테이블에서 키워드를 선택해서 비교해보세요'}
                </span>
              </div>
              <span style={{ color: '#4A5568', fontSize: 12 }}>{selCollapsed ? '▼ 펼치기' : '▲ 접기'}</span>
            </div>
            {!selCollapsed && (
              <div style={{ padding: '0 20px 20px' }}>
                {selLoading ? (
                  <div style={{ color: '#8892A4', fontSize: 13, textAlign: 'center' as const, padding: 20 }}>분석 중...</div>
                ) : selDetail ? (
                  <DetailCard data={selDetail} period={selPeriod} loading={selLoading} isMain={false}
                    onClose={() => { setSelDetail(null); setSelCollapsed(true); }}
                    onPeriodChange={async (p: string) => {
                      setSelPeriod(p);
                      setSelLoading(true);
                      try { const res = await fetchDetail(selDetail.keyword, p); setSelDetail(res); }
                      finally { setSelLoading(false); }
                    }} />
                ) : (
                  <div style={{ color: '#4A5568', fontSize: 13, textAlign: 'center' as const, padding: '20px 0' }}>
                    아래 테이블에서 키워드를 클릭하면 여기에 표시됩니다
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 연관 키워드 테이블 */}
        {sorted.length > 0 && (
          <div style={{ ...s.card, overflowX: 'auto' }}>
            <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 12 }}>
              {filtered.length}개 표시 {filterEnabled && results.length !== filtered.length ? `(전체 ${results.length}개 중)` : ''}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' as const, minWidth: 1000 }}>
              <thead>
                <tr style={{ borderBottom: '0.5px solid #2A3045' }}>
                  {[['keyword','키워드'],['total','총검색량'],['pc','PC'],['mobile','모바일'],['mob_ratio','모바일%'],['competition','경쟁도'],['pc_click','PC클릭'],['mob_click','M클릭'],['total_click','총클릭'],['pc_ctr','PC CTR'],['mob_ctr','M CTR'],['total_ctr','클릭률']].map(([col, label]) => (
                    <th key={col} style={{ ...s.th, textAlign: col === 'keyword' ? 'left' as const : 'right' as const }} onClick={() => handleSort(col)}>
                      {label}{sortCol === col ? (sortDesc ? ' ▼' : ' ▲') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid #1A2035', background: selDetail?.keyword === r.keyword ? '#1A2A3A' : 'transparent' }}>
                    <td style={{ padding: '8px 6px', color: '#00D4AA', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => handleSelectKeyword(r.keyword)}>{r.keyword}</td>
                    <td style={{ padding: '8px 6px', width: 100 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 30, height: 6, background: '#0F1117', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                          <div style={{ width: `${(r.total / maxTotal) * 100}%`, height: '100%', background: i === 0 ? '#E53E3E' : '#3182CE', borderRadius: 2 }} />
                        </div>
                        <span style={{ color: '#E8ECF4', fontSize: 12, width: 50, textAlign: 'right' as const, flexShrink: 0 }}>{r.total.toLocaleString()}</span>
                      </div>
                    </td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.pc.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.mobile.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.mob_ratio}%</td>
                    <td style={{ padding: '8px 6px', color: compColor(r.competition), fontSize: 11, textAlign: 'right' as const }}>{r.competition}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.pc_click.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.mob_click.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#E8ECF4', fontSize: 12, textAlign: 'right' as const, fontWeight: 600 }}>{r.total_click.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.pc_ctr}%</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' as const }}>{r.mob_ctr}%</td>
                    <td style={{ padding: '8px 6px', color: '#00D4AA', fontSize: 12, textAlign: 'right' as const, fontWeight: 600 }}>{r.total_ctr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
